import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, 
  Video, 
  FolderOpen, 
  Play, 
  Plus, 
  Eye, 
  Settings, 
  LogOut,
  Monitor
} from 'lucide-react';
import VideosTab from './VideosTab';
import CategoriesTab from './CategoriesTab';
import AdsTab from './AdsTab';
import BannerAdsTab from './BannerAdsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vastAds, setVastAds] = useState([]);
  const [bannerAds, setBannerAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    // Set up axios default header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Load initial data
    loadDashboardData();
  }, [navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchVideos(),
        fetchCategories(),
        fetchVastAds(),
        fetchBannerAds()
      ]);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/admin/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchVastAds = async () => {
    try {
      const response = await axios.get(`${API}/admin/vast-ads`);
      setVastAds(response.data);
    } catch (error) {
      console.error('Error fetching VAST ads:', error);
    }
  };

  const fetchBannerAds = async () => {
    try {
      const response = await axios.get(`${API}/admin/banner-ads`);
      setBannerAds(response.data);
    } catch (error) {
      console.error('Error fetching banner ads:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="loading-spinner"></div>
          <span className="text-white">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="admin-sidebar border-b border-white/10 p-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-brand font-bold text-white">
            usexxx Admin
          </h1>
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => window.open('/', '_blank')}
              className="text-white/70 hover:text-white"
              data-testid="view-site"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Site
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-white/70 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 admin-sidebar p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} orientation="vertical">
            <TabsList className="grid grid-cols-1 gap-2 bg-transparent h-auto">
              <TabsTrigger value="dashboard" className="justify-start text-white/70 hover:text-white data-[state=active]:bg-white/10">
                <BarChart3 className="h-4 w-4 mr-2" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="videos" className="justify-start text-white/70 hover:text-white data-[state=active]:bg-white/10">
                <Video className="h-4 w-4 mr-2" />
                Videos
              </TabsTrigger>
              <TabsTrigger value="categories" className="justify-start text-white/70 hover:text-white data-[state=active]:bg-white/10">
                <FolderOpen className="h-4 w-4 mr-2" />
                Categories
              </TabsTrigger>
              <TabsTrigger value="ads" className="justify-start text-white/70 hover:text-white data-[state=active]:bg-white/10">
                <Play className="h-4 w-4 mr-2" />
                VAST Ads
              </TabsTrigger>
              <TabsTrigger value="banners" className="justify-start text-white/70 hover:text-white data-[state=active]:bg-white/10">
                <Monitor className="h-4 w-4 mr-2" />
                Banner Ads
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </aside>

        {/* Main Content */}
        <main className="flex-1 admin-content p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsContent value="dashboard">
              <DashboardTab stats={stats} />
            </TabsContent>
            <TabsContent value="videos">
              <VideosTab videos={videos} categories={categories} onRefresh={fetchVideos} />
            </TabsContent>
            <TabsContent value="categories">
              <CategoriesTab categories={categories} onRefresh={fetchCategories} />
            </TabsContent>
            <TabsContent value="ads">
              <AdsTab vastAds={vastAds} onRefresh={fetchVastAds} />
            </TabsContent>
            <TabsContent value="banners">
              <BannerAdsTab bannerAds={bannerAds} onRefresh={fetchBannerAds} />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

// Dashboard Tab Component
const DashboardTab = ({ stats }) => {
  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-brand font-bold text-white">Dashboard Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Videos"
          value={stats.total_videos || 0}
          icon={<Video className="h-6 w-6" />}
          color="from-blue-500 to-cyan-500"
        />
        <StatCard
          title="Categories"
          value={stats.total_categories || 0}
          icon={<FolderOpen className="h-6 w-6" />}
          color="from-green-500 to-emerald-500"
        />
        <StatCard
          title="VAST Ads"
          value={stats.total_vast_ads || 0}
          icon={<Play className="h-6 w-6" />}
          color="from-purple-500 to-pink-500"
        />
        <StatCard
          title="Banner Ads"
          value={stats.total_banner_ads || 0}
          icon={<Monitor className="h-6 w-6" />}
          color="from-green-500 to-blue-500"
        />
        <StatCard
          title="Total Views"
          value={stats.total_views || 0}
          icon={<Eye className="h-6 w-6" />}
          color="from-orange-500 to-red-500"
        />
      </div>
      
      <Card className="glass-dark border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button className="btn-hover bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Add Video
            </Button>
            <Button className="btn-hover bg-gradient-to-r from-green-600 to-blue-600 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
            <Button className="btn-hover bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Add VAST Ad
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const StatCard = ({ title, value, icon, color }) => {
  return (
    <Card className="glass-dark border-white/20 card-hover">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/70 text-sm font-medium">{title}</p>
            <p className="text-3xl font-bold text-white">{value.toLocaleString()}</p>
          </div>
          <div className={`p-3 rounded-full bg-gradient-to-r ${color}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminDashboard;